import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PaymentFlowItemWithProcessors, useDuplicatePaymentFlow } from '@/lib/api/paymentFlows';
import { useOrgNavigate } from '@/hooks/use-app-navigate';

type Props = {
  flow: PaymentFlowItemWithProcessors | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Asks for a name, creates a copy of the flow under it, and lands on the copy's edit page
 * so the merchant can immediately change what the copy is for (a card brand, a processor).
 */
const DuplicateForm = ({ flow, onOpenChange }: { flow: PaymentFlowItemWithProcessors } & Pick<Props, 'onOpenChange'>) => {
  const { t } = useTranslation(['paymentFlows', 'common']);
  const { orgRoutes, navigate } = useOrgNavigate();
  const duplicatePaymentFlow = useDuplicatePaymentFlow();
  const [name, setName] = useState(t('paymentFlows:duplicate.default_name', { name: flow.name }));

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !duplicatePaymentFlow.isPending;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;

    try {
      const copy = await duplicatePaymentFlow.mutateAsync({ flowId: flow.id, name: trimmedName });
      onOpenChange(false);
      navigate(orgRoutes.PAYMENT_FLOW_EDIT(copy.id));
    } catch {
      // Error is handled by the mutation hook (toast)
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogHeader>
        <DialogTitle>{t('paymentFlows:duplicate.title')}</DialogTitle>
        <DialogDescription>{t('paymentFlows:duplicate.description', { name: flow.name })}</DialogDescription>
      </DialogHeader>
      <div className="space-y-2 py-4">
        <Label htmlFor="payment-flow-duplicate-name">{t('paymentFlows:duplicate.name_label')}</Label>
        <Input
          id="payment-flow-duplicate-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('paymentFlows:duplicate.name_placeholder')}
          autoFocus
          disabled={duplicatePaymentFlow.isPending}
        />
      </div>
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={duplicatePaymentFlow.isPending}
        >
          {t('common:cancel')}
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          {duplicatePaymentFlow.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
          {t('paymentFlows:duplicate.action')}
        </Button>
      </DialogFooter>
    </form>
  );
};

export const PaymentFlowDuplicateDialog = ({ flow, open, onOpenChange }: Props) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {/* Keyed on the flow so the name field resets for each flow being copied */}
        {flow && <DuplicateForm key={flow.id} flow={flow} onOpenChange={onOpenChange} />}
      </DialogContent>
    </Dialog>
  );
};
